/** Operations → Safety meeting: one-tap CSV / PDF export of the log for a date range. */
import { useState } from "react";
import { FileDown, Loader2, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SendToCloud from "./SendToCloud";

const today = () => new Date().toISOString().slice(0, 10);
const ago = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

export default function SafetyExport({ day }) {
  const [from, setFrom] = useState(day || ago(30));
  const [to, setTo] = useState(day || today());
  const [busy, setBusy] = useState("");

  const download = async (format) => {
    setBusy(format);
    try {
      const { data } = await api.get("/safety/log/export", { params: { from_day: from, to_day: to, format }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `safety-meeting-log_${from}_${to}.${format}` });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Export failed"); }
    finally { setBusy(""); }
  };
  const nudge = async () => {
    setBusy("nudge");
    try {
      const { data } = await api.post("/safety/nudge/run");
      toast.success(data.nudged ? `Reminder sent to ${data.nudged} ${data.nudged === 1 ? "person" : "people"}` : "Everyone has already confirmed");
    } catch { toast.error("Could not send reminders"); }
    finally { setBusy(""); }
  };

  const btn = "inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-xs font-semibold text-white/80 hover:bg-white/10 disabled:opacity-50";
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2 text-xs" data-testid="safety-export">
      <span className="font-semibold text-white/70">Export for inspection</span>
      <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-white" data-testid="safety-export-from" />
      <span className="text-white/40">to</span>
      <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-white" data-testid="safety-export-to" />
      <button onClick={() => download("csv")} disabled={!!busy} className={btn} data-testid="safety-export-csv">{busy === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} CSV</button>
      <button onClick={() => download("pdf")} disabled={!!busy} className={btn} data-testid="safety-export-pdf">{busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF</button>
      <SendToCloud name={`safety-meeting-log_${from}_${to}.pdf`} apiPath={`/api/safety/log/export?from_day=${from}&to_day=${to}&format=pdf`} category="Safety records" label="Send PDF to cloud" />
      <button onClick={nudge} disabled={!!busy} className={`${btn} ml-auto border-amber-400/40 text-amber-200`} data-testid="safety-nudge-now">{busy === "nudge" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />} Remind unconfirmed now</button>
    </div>
  );
}
